/**
 * O menu da barra lateral — e, por tabela, o nome e o ícone de cada tela.
 *
 * <p>Isto morava dentro do `AppShell`, e por isso cada página inventava o próprio
 * cabeçalho: a barra dizia "Mapa da Ilha" com um navio, e a tela dizia "Mapa do Mundo"
 * com um círculo. Eram dois lugares dizendo o nome da mesma coisa, e eles divergiram.
 *
 * <p>Agora o menu é a fonte da verdade dos dois: a barra desenha os itens a partir
 * daqui, e {@link TituloDaPagina} lê a mesma lista para montar o cabeçalho. Renomear
 * uma tela é mexer numa linha só.
 */

export interface ItemNav {
  rota: string;
  rotulo: string;
  /** O id do desenho em {@link IconeDoMenu}. */
  icone: string;
  /**
   * Quando presente, o item mostra quantas fichas existem sob esta chave.
   *
   * <p>Quase todas vêm da contagem do mundo (`racas`, `npcs`, `locais`…). A exceção é
   * {@code documentos}, que vem do acervo — o AppShell junta as duas origens num mapa
   * só para que "quantos existem aqui" continue sendo uma pergunta com uma resposta,
   * e não duas rotinas de contagem convivendo no mesmo componente.
   */
  colecao?: string;
  /**
   * Não entra no total que a seção mostra quando está fechada.
   *
   * <p>Existe por causa da Equipe. O total é a soma das coleções da seção, e ele só
   * significa alguma coisa enquanto elas são da mesma natureza: 11 documentos mais 3
   * transcrições são 14 arquivos. Somar 6 pessoas a isso dá 20 do quê? A resposta é
   * "nada", e um número que não responde a pergunta nenhuma é pior que número nenhum.
   *
   * <p>O contador do próprio item continua aparecendo — a exclusão é só do agregado.
   */
  foraDoTotal?: boolean;
  /**
   * O que este item <b>mede</b> — que não é a mesma pergunta de {@link colecao}.
   *
   * <p>{@code colecao} responde "quantas fichas existem aqui dentro", e é um número de
   * inventário: 26 locais. {@code medida} responde "quanto do caminho já foi andado", e
   * por isso ganha a barra embaixo.
   *
   * <p>Separá-las foi o que permitiu a barra sair de <b>Regiões e Áreas</b> e ir para
   * <b>Progresso da Ilha</b> (pedido do Theo). Regiões e Áreas voltou a dizer só quantas
   * áreas existem — que é o que se espera de um item de listagem —, e o progresso ficou
   * no item cujo nome é exatamente esse.
   *
   * <p>Não traz número nenhum: o valor vem do painel de Progresso, que é onde a régua
   * é combinada. Fixar um número aqui foi o que fez o menu dizer 16/49 enquanto a
   * página dizia 16/21 — duas réguas para a mesma coisa, e nenhuma das duas errada
   * por si. Régua duplicada diverge; é só questão de quando.
   *
   * <p>São duas, e cada uma lê o número da <b>própria</b> página: a da Season vem do
   * painel, a do Roadmap vem do KPI "Progresso geral" da timeline. Nenhuma das duas
   * calcula nada aqui — ver o parágrafo acima sobre régua duplicada.
   */
  medida?: 'progresso-da-season' | 'progresso-do-roadmap' | 'progresso-do-quadro';
}

export interface Secao {
  titulo: string;
  /**
   * A cor da seção, que é a cor de tudo dentro dela.
   *
   * <p>A cor aqui responde <b>onde você está</b>, e não o que cada item é. Foi decisão
   * do Theo depois de ver as duas: com quinze cores, a barra vira um mostruário e o
   * olho não agrupa nada; com quatro, a mancha de cor diz de relance em que parte do
   * produto você está antes de você ler uma palavra.
   *
   * <p>O que se perdeu vale registro: NPC deixa de ser azul e criatura deixa de ser
   * vermelha, cores que elas têm nos badges da aba Cenas. Lá dentro a regra continua
   * valendo — ela só não sobe mais para o menu.
   */
  cor: string;
  itens: ItemNav[];
}

/**
 * Os itens do menu.
 *
 * <p>Não há mais item externo: Cenas era o último, e virou tela nativa. Com ele foi
 * embora o ramo que abria link em outra aba — quando voltar a existir um, ele volta
 * junto, em vez de ficar aqui esperando.
 */
export const SECOES: Secao[] = [
  {
    titulo: 'Gestão de Projeto',
    cor: '#C9A25A',
    itens: [
      { rota: '/roadmap', rotulo: 'Roadmap', icone: 'mapa', medida: 'progresso-do-roadmap' },
      // Logo abaixo do Roadmap, e não numa seção própria: é o mesmo trabalho visto de
      // outro jeito — o Roadmap responde "quando", o Dev Board responde "em que pé".
      // Barra própria: o Dev Board mede a semana, o Roadmap mede os doze meses. São
      // duas perguntas diferentes, e por isso os dois números convivem sem se somar.
      { rota: '/dev-board', rotulo: 'Dev Board', icone: 'quadro', medida: 'progresso-do-quadro' },
      // O número é quem tem acesso ao GG Pilot — o mesmo que a página lista.
      { rota: '/equipe', rotulo: 'Equipe', icone: 'pessoas', colecao: 'equipe', foraDoTotal: true },
      { rota: '/documentos', rotulo: 'Documentos', icone: 'documento', colecao: 'documentos' },
      // O cofre do time: URL e contas de teste de cada ambiente. Sem `colecao` — o
      // contador do mundo não responde por ambientes, e um número aqui não diria nada.
      { rota: '/acessos', rotulo: 'Acessos', icone: 'chave' },
      // Logo abaixo de Documentos, e não dentro dele: as duas guardam texto, mas o
      // acervo responde "onde está a spec" e a transcrição responde "o que ficou
      // decidido". Uma é agrupada por assunto, a outra é uma fila de reuniões.
      {
        rota: '/transcricoes',
        rotulo: 'Transcrições',
        icone: 'transcricao',
        colecao: 'transcricoes',
      },
      // Fica em Gestão de Projeto, e não em Criação do Mundo, porque a pergunta que
      // ela responde é de entrega — "dá para lançar?" — e não de lore.
      //
      // A barra mora aqui, e não em Regiões e Áreas: é neste item que alguém procura
      // quanto falta para a Season sair.
      //
      // Vem depois de Documentos por pedido do Theo. As duas medidas da seção ficam
      // assim nas pontas — Roadmap no alto, Season embaixo —, e entre elas o que é
      // acervo: Equipe e Documentos contam coisas, não medem caminho andado.
      { rota: '/season1', rotulo: 'Season 1', icone: 'barras', medida: 'progresso-da-season' },
    ],
  },
  {
    titulo: 'Marketing e Branding',
    cor: '#B04A45',
    itens: [
      { rota: '/marketing', rotulo: 'Visão geral', icone: 'olho' },
      { rota: '/campanhas', rotulo: 'Campanhas', icone: 'espada' },
    ],
  },
  {
    titulo: 'Ferramentas',
    cor: '#C9A25A',
    itens: [{ rota: '/codex/imagens', rotulo: 'Imagens com Codex', icone: 'imagem' }],
  },
  /**
   * Entre Marketing e Criação do Mundo, e não dentro dela, porque a pergunta é outra.
   *
   * <p><b>Criação do Mundo</b> responde "o que existe na ilha" — a geografia, as cenas,
   * quem mora onde. <b>Game Design</b> responde "como se joga com isso": a curva de
   * dificuldade, os tiers, as mecânicas de coleta e criação. A mesma área aparece nas
   * duas, e diz coisas diferentes em cada uma — em Criação do Mundo, Mata das Serpentes
   * é um lugar com narrativa e criaturas; aqui, é um T4.
   */
  {
    titulo: 'Game Design',
    cor: '#5A93C4',
    itens: [
      /*
       * Cenas, Ações e Fluxo vem primeiro — decisão do Theo, e ela troca o critério da
       * ordem. Antes a lista seguia a leitura de baixo para cima (quem o personagem é,
       * como ele luta, o que ele tira do mundo); agora ela começa pelo motor.
       *
       * Faz sentido: esta é a única das quatro que descreve o sistema em que as outras
       * três acontecem — cena, ação, condição e efeito são o vocabulário que a ficha, o
       * combate e a tier list usam sem definir. E é a que o time abre todo dia, porque é
       * onde as decisões pendentes estão.
       *
       * As rotas não acompanham os renomes (`ficha`, `areas-e-tier`): renomear o item é
       * de graça, renomear a URL quebra os links que já existem. O item do menu é a
       * única fonte do nome, então a sidebar e o cabeçalho da página mudam juntos daqui.
       */
      /*
       * O GDD vem primeiro — pedido do Theo, 02/09/2026. É o documento que as outras
       * páginas resumem e reconciliam: quem chega ao Game Design pelo começo lê o que
       * o jogo é antes de ler o que cada tela extraiu dele. As outras quatro seguem na
       * ordem de sempre, do motor para a ficha.
       */
      { rota: '/game-design/gdd', rotulo: 'GDD', icone: 'documento' },
      { rota: '/game-design/cenas-acoes-fluxo', rotulo: 'Cenas, Ações e Fluxo de Jogo', icone: 'fluxo' },
      /*
       * Personagem é o resumo da ficha inteira — épicos 3/4/5 + GDD reconciliados, com
       * os conflitos na mesa. A Ficha, logo abaixo, é a bancada onde o time ESCREVE as
       * regras (bônus, fórmulas, rolagens) — ela se chamava "Personagem" e cedeu o nome
       * quando o resumo nasceu (pedido do Theo, 19/08/2026); a rota dela não muda, pela
       * mesma razão de sempre: renomear o item é de graça, renomear a URL quebra links.
       */
      { rota: '/game-design/personagem', rotulo: 'Personagem', icone: 'personagem' },
      // Logo abaixo de Personagem: a ficha numa página, o que acontece com ela noutra
      // (pedido do Theo, 19/08/2026). Vigor, Carga, Fome, Frio, os status de combate.
      { rota: '/game-design/condicoes-do-personagem', rotulo: 'Condições do Personagem', icone: 'pocao' },
      { rota: '/game-design/ficha', rotulo: 'Ficha', icone: 'personagem' },
      { rota: '/game-design/combate', rotulo: 'Combate', icone: 'espada' },
      // Logo abaixo de Combate: o motor numa página, o catálogo noutra — a planilha
      // do Theo (22/08/2026) é a fonte do catálogo.
      { rota: '/game-design/acoes-de-combate', rotulo: 'Ações de Combate', icone: 'espada' },
      { rota: '/game-design/areas-e-tier', rotulo: 'Tier List', icone: 'hexagono-feito' },
      // Logo abaixo da Tier List: lá se escolhe até que tier cada perícia vai por área,
      // aqui se lê o que o mundo junta em torno dela — e o mapa daqui é o de lá.
      { rota: '/game-design/pericias', rotulo: 'Perícias', icone: 'bigorna' },
    ],
  },
  /**
   * A antiga "Criação do Mundo" virou duas seções, porque as telas respondiam a
   * perguntas diferentes debaixo do mesmo título.
   *
   * <p><b>Criação do Mundo</b> é a ilha e o que se passa nela: o mapa, as cenas, as
   * áreas e os locais. É onde o mundo é desenhado.
   *
   * <p><b>Banco de Dados</b> é o catálogo do que povoa esse desenho — raças, facções,
   * NPCs, criaturas, itens, quests e eventos. São fichas que existem por si, e que as
   * telas de cima referenciam.
   *
   * <p>Dois itens saíram, e nenhuma rota foi removida junto com eles:
   *
   * <p><b>Regiões</b>, porque a listagem de Áreas já agrupa por região e o cabeçalho de
   * cada bloco abre a ficha da região — eram duas portas para a mesma tela.
   *
   * <p><b>Tipos de local</b>, porque tipo só significa alguma coisa dentro de Locais, e
   * agora é um botão lá. Os chips de vínculo continuam levando às duas fichas.
   */
  {
    titulo: 'Criação do Mundo',
    cor: '#A87BD0',
    itens: [
      { rota: '/mundo/mapa', rotulo: 'Mapa da Ilha', icone: 'ilha' },
      // Sem contador de propósito — a cena não é ficha do mundo, e o endpoint de
      // contagem responde pelas dez coleções, não por ela.
      { rota: '/mundo/cenas', rotulo: 'Visão das Cenas', icone: 'sol' },
      // "Calendário", e não "Season": o item Season 1 de Gestão de Projeto mede o
      // progresso da escrita; este desenha os dias da temporada. Sem contador, pela
      // mesma razão das cenas — season não é ficha do mundo.
      { rota: '/mundo/calendario', rotulo: 'Calendário', icone: 'calendario' },
      // Só o número de áreas. A barra e o "x/49" foram para Progresso da Ilha: aqui
      // eles diziam quanto do mundo falta, no meio de uma lista de portas para telas.
      { rota: '/mundo/areas', rotulo: 'Regiões e Áreas', icone: 'alvo', colecao: 'areas' },
      { rota: '/mundo/locais', rotulo: 'Locais', icone: 'losango', colecao: 'locais' },
    ],
  },
  {
    titulo: 'Banco de Dados',
    cor: '#7EA65B',
    itens: [
      { rota: '/mundo/racas', rotulo: 'Raças', icone: 'elfo', colecao: 'racas' },
      { rota: '/mundo/faccoes', rotulo: 'Facções', icone: 'estandarte', colecao: 'faccoes' },
      { rota: '/mundo/npcs', rotulo: 'NPCs', icone: 'personagem', colecao: 'npcs' },
      {
        rota: '/mundo/criaturas',
        rotulo: 'Criaturas e Inimigos',
        icone: 'felino',
        colecao: 'criaturas',
      },
      { rota: '/mundo/itens', rotulo: 'Itens', icone: 'pocao', colecao: 'itens' },
      { rota: '/mundo/quests', rotulo: 'Quests', icone: 'rosacea', colecao: 'quests' },
      { rota: '/mundo/eventos', rotulo: 'Eventos', icone: 'losango-duplo', colecao: 'eventos' },
      { rota: '/mundo/cartas', rotulo: 'Cartas', icone: 'carta', colecao: 'cartas' },
    ],
  },
  /**
   * A última seção, e a única que não é sobre o projeto: Mistik Dream é a mesa de RPG
   * experimental que o time joga para provar as regras que as outras telas descrevem.
   *
   * <p>Fica por último porque é a de menor frequência — quem abre o GG Pilot para
   * trabalhar não passa por aqui —, e porque separá-la deixa claro que o que se escreve
   * numa ficha é estado de uma PARTIDA, e não do produto.
   */
  {
    titulo: 'Mistik Dreams',
    cor: '#B07BC4',
    itens: [
      { rota: '/mistik-dream/fichas', rotulo: 'Fichas', icone: 'personagem' },
      // Depois das fichas: o registro é o que o grupo consulta DURANTE a sessão, e as
      // fichas são o que se monta antes dela.
      { rota: '/mistik-dream/registro', rotulo: 'Registro do Grupo', icone: 'dado' },
    ],
  },
];

/** O destino do menu, com a cor da seção a que ele pertence. */
export interface Destino extends ItemNav {
  secao: string;
  cor: string;
}

const POR_ROTA = new Map<string, Destino>();
SECOES.forEach((s) =>
  s.itens.forEach((i) => POR_ROTA.set(i.rota, { ...i, secao: s.titulo, cor: s.cor }))
);

/**
 * O item de menu de um caminho — inclusive quando o caminho é a ficha de uma coleção.
 *
 * <p>`/mundo/racas/3` é a ficha de uma raça e não tem item próprio no menu; quem
 * responde por ela é `/mundo/racas`. Por isso o id no fim é descartado antes da
 * segunda tentativa.
 */
export function destinoDa(caminho: string): Destino | undefined {
  return POR_ROTA.get(caminho) ?? POR_ROTA.get(caminho.replace(/\/[^/]+$/, ''));
}
